import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAllBreathPages,
  getHistoryInbody,
  getHistoryLabPanels,
  getPnoeMeasurements,
} from "../../../../common/network/Api";
import {
  PnoeMeasurementCatalogs,
  PnoeMeasurementListItem,
} from "../../entities/pnoeMeasurement";
import { describeBreathError } from "../orders/usePnoeMeasurements";
import {
  AssessmentHistoryRow,
  inbodyToRow,
  labPanelToRow,
  normalizeClientId,
  pnoeMeasurementToRow,
  sortAssessmentRows,
} from "./assessmentHistory";
import { HistoryScope } from "./useHistoryScope";
import useHistoryList from "./useHistoryList";

/**
 * The merged Assessments listing.
 *
 * Three sources with nothing in common get pulled into one sorted list:
 *
 * - **Lab panels** — the paged `/history/labpanels` listing, every page
 *   loaded through `useHistoryList` (the filters need all the rows).
 * - **InBody tests** — the paged `/history/inbody` listing, likewise.
 * - **PNOĒ tests** — a separate service, cursor-paginated. The table filters
 *   client-side over one merged array, so the pages are walked to the end up
 *   front (`fetchAllBreathPages`). No `customer_id` filter is sent in any
 *   scope: the service scopes the listing to the token. The company view
 *   sends the affiliate's token, the customer's own page the customer's, and
 *   the client workspace the selected client's token (the per-tab customer
 *   session), so a client's page is served exactly like that customer's own.
 *
 * The sources **fail independently**. The breath service is a different
 * origin with its own auth, so a failure there must not take the other rows
 * down with it — the table shows what it has plus a banner.
 *
 * All three take the logged-in user's token, so they serve the customer's own
 * page too. As of 2026-10-02 the breath service answers a customer token
 * with an empty list, so the customer sees no PNOĒ tests until the backend
 * scopes the listing to the customer's own tests.
 */

const PNOE_PAGE_LIMIT = 100;
// 100 pages of 100 is well past any real company's history, and bounds a
// runaway cursor.
const PNOE_MAX_PAGES = 100;

export interface AssessmentHistoryResult {
  rows: AssessmentHistoryRow[];
  loading: boolean;
  /** Set when the lab-panels listing failed — lab rows are missing. */
  labError: string | null;
  /** Set when the breath service failed — PNOĒ rows are missing. */
  pnoeError: string | null;
  /** True when the breath listing was cut off by the page cap. */
  truncated: boolean;
  /** Set when the InBody listing failed — InBody rows are missing. */
  inbodyError: string | null;
  /** Lab or InBody pages still arriving; rows and filter options incomplete. */
  listsLoadingRest: boolean;
  listsLoaded: number;
  listsTotal: number | null;
  /** PNOĒ tests could not be loaded, so they are missing from `rows`. */
  partial: boolean;
  reload: () => void;
}

const useAssessmentHistory = (
  scope: HistoryScope,
  catalogs: PnoeMeasurementCatalogs | null
): AssessmentHistoryResult => {
  const { kind, subjectId } = scope;

  const listUserid = kind === "company" ? undefined : subjectId;
  const listsEnabled = kind === "company" || Boolean(subjectId);

  const labs = useHistoryList({
    fetchPage: getHistoryLabPanels,
    cacheKey: "labpanels",
    userid: listUserid,
    enabled: listsEnabled,
    // A panel's status moves (approved, paid, results in) without the count
    // or the newest id changing.
    rowVersion: (row: any) => String(row?.panelStatus),
  });
  const inbody = useHistoryList({
    fetchPage: getHistoryInbody,
    cacheKey: "inbody",
    userid: listUserid,
    enabled: listsEnabled,
  });

  const [measurements, setMeasurements] = useState<PnoeMeasurementListItem[]>(
    []
  );
  const [breathLoading, setBreathLoading] = useState(true);
  const [pnoeError, setPnoeError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Sequences responses so a slow reply for an abandoned scope can't overwrite
  // the current one — same guard `usePnoeMeasurements` used.
  const requestIdRef = useRef(0);

  // The client workspace signs with the selected client's token. Its subject
  // is read from that same session, so waiting for the subject also waits
  // for the token.
  const asSelectedClient = kind === "client";
  const breathReady = !asSelectedClient || Boolean(subjectId);

  useEffect(() => {
    if (!breathReady) {
      setBreathLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    let cancelled = false;

    setBreathLoading(true);
    setPnoeError(null);
    setTruncated(false);

    fetchAllBreathPages<PnoeMeasurementListItem>(
      (params) => getPnoeMeasurements(params, { asSelectedClient }),
      { limit: PNOE_PAGE_LIMIT, maxPages: PNOE_MAX_PAGES }
    )
      .then(({ data, truncated: cutOff }) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setMeasurements(data);
        setTruncated(cutOff);
      })
      .catch((error) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        console.error("Error loading PNOĒ tests for history:", error);
        setMeasurements([]);
        setPnoeError(describeBreathError(error));
      })
      .finally(() => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setBreathLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [breathReady, asSelectedClient, subjectId, reloadToken]);

  const rows = useMemo(() => {
    const merged = [
      ...labs.rows.map(labPanelToRow),
      ...measurements.map((measurement) =>
        pnoeMeasurementToRow(measurement, catalogs)
      ),
      ...inbody.rows.map(inbodyToRow),
    ];
    // The history listings are already narrowed by `userid`; this keeps the
    // merged list honest for the breath rows too. Ids are compared lower-cased
    // — the breath service writes GUIDs in upper case, the platform in lower.
    const subject = normalizeClientId(subjectId);
    const scoped = subject
      ? merged.filter((row) => row.clientId === subject)
      : merged;
    return sortAssessmentRows(scoped);
  }, [labs.rows, measurements, inbody.rows, catalogs, subjectId]);

  const reloadLabs = labs.reload;
  const reloadInbody = inbody.reload;
  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
    reloadLabs();
    reloadInbody();
  }, [reloadLabs, reloadInbody]);

  const listsTotal =
    labs.totalCount === null && inbody.totalCount === null
      ? null
      : (labs.totalCount ?? 0) + (inbody.totalCount ?? 0);

  return {
    rows,
    loading: breathLoading || labs.loading || inbody.loading,
    labError: labs.error,
    pnoeError,
    truncated,
    inbodyError: inbody.error,
    listsLoadingRest: labs.loadingRest || inbody.loadingRest,
    listsLoaded: labs.rows.length + inbody.rows.length,
    listsTotal,
    partial: Boolean(pnoeError),
    reload,
  };
};

export default useAssessmentHistory;
